import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PageMeta from '../components/PageMeta';
import { Capacitor } from "@capacitor/core";
import { CalendarCheck, Plus, Trash, Sidebar, X, Heartbeat, Drop, ArrowRight, ArrowUp, UsersThree, CalendarBlank, CircleNotch, BookOpen, ClockCounterClockwise, Bell, SlidersHorizontal, ListChecks, Pause, Play, CheckCircle, Paperclip, Camera, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import AIDataConsentModal from "../components/legal/AIDataConsentModal";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import BottomSheet from '../components/BottomSheet';
import ConfirmModal from '../components/ConfirmModal';
import type { ArtifactContent, ChatAttachment } from "@researcher/shared";
import { uploadAttachment, validateFile, MAX_ATTACHMENTS_PER_MESSAGE, type PendingAttachment } from "../utils/chatUploads";
import AttachmentPreview from "../components/chat/AttachmentPreview";
import { apiUrl } from "../config/api";
import { reportError } from "../utils/errorReporting";
import GentleToast from "../components/GentleToast";
import MessageActions from "../components/chat/MessageActions";
import StreamingText from "../components/chat/StreamingText";
import VoiceInputButton from "../components/chat/VoiceInputButton";
import UserMessage from "../components/chat/UserMessage";
import AssistantMessage from "../components/chat/AssistantMessage";
import LoadingDots from "../components/chat/LoadingDots";
import SourcesFooter from "../components/chat/SourcesFooter";
import SuggestedReplies from "../components/chat/SuggestedReplies";
import { inferSuggestedReplies } from "../utils/inferSuggestedReplies";
import { sanitizeAssistantText } from "../utils/sanitizeAssistantText";
import { submitFeedback, removeFeedback } from "../utils/feedback";
import { useKeyboardHeight } from "../hooks/useKeyboardHeight";

const isNative = Capacitor.isNativePlatform();

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=health.leena.app';
const APP_STORE_URL = 'https://apps.apple.com/us/app/romy/id6760265825';

function detectMobileOS(): 'ios' | 'android' | null {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return null;
}
const mobileOS = detectMobileOS();

const IOS_ICON = (
  <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 21.99C7.78997 22.03 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
  </svg>
);
const ANDROID_ICON = (
  <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.18 23.76C2.56 23.45 2.18 22.82 2.18 22.12V1.88C2.18 1.18 2.56 0.55 3.18 0.24L13.54 12L3.18 23.76ZM16.36 15.15L5.27 21.6L14.12 12.58L16.36 15.15ZM20.16 10.81C20.68 11.14 21 11.7 21 12.31C21 12.64 20.9 12.97 20.7 13.25L18.84 14.34L16.36 11.69L18.84 9.04L20.16 10.81ZM5.27 2.4L16.36 8.85L14.12 11.42L5.27 2.4Z" />
  </svg>
);

function AppBadgeBanner({ onDismiss }: { onDismiss?: () => void }) {
  return (
    <div className="md:hidden sticky top-0 z-10 p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-gray-900 dark:to-gray-900 border-b border-orange-200 dark:border-orange-800 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-800 dark:text-gray-100 mb-1">
            Get more from Romy
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 leading-relaxed">
            Download the app to save your chat history and get personalized insights.
          </p>
          <div className="flex gap-1.5">
            {mobileOS !== 'android' && APP_STORE_URL && (
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2.5 py-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg">
                {IOS_ICON}
                <span className="text-[10px] font-semibold leading-none">App Store</span>
              </a>
            )}
            {mobileOS !== 'ios' && (
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2.5 py-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg">
                {ANDROID_ICON}
                <span className="text-[10px] font-semibold leading-none">Google Play</span>
              </a>
            )}
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="p-1 -mr-1 -mt-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
import ReactMarkdown from "react-markdown";
import { MARKDOWN_COMPONENTS } from "../components/chat/MarkdownContent";
import LoginModal from "../components/LoginModal";
import AppNav from "../components/AppNav";
import HealthSummaryPanel from "../components/health/HealthSummaryPanel";
import ArtifactCard from "../components/chat/ArtifactCard";
import ResearchCard from "../components/chat/ResearchCard";
import ProfileUpdateCard from "../components/chat/ProfileUpdateCard";
import TrackingProtocolProposalCard from "../components/chat/TrackingProtocolProposalCard";
import ProposedRecordsCard from "../components/chat/ProposedRecordsCard";
import HealthAnalysisCard from "../components/chat/HealthAnalysisCard";
import GoalPlanCard from "../components/chat/GoalPlanCard";
import MealPlanCard from "../components/chat/MealPlanCard";
import RomyIcon from "../components/RomyIcon";
import { getHealthLogs, type HealthLogEntry } from "../utils/healthLog";
import { getAppointments } from "../utils/appointments";
import type { Appointment } from "@researcher/shared";
import {
  getUserChats,
  createChat,
  updateChat,
  deleteChat,
  getChat,
  ChatConversation,
  ChatMessage as ChatMessageType
} from "../utils/chatHistory";
import { useChatSession, type ChatMessage as Message, type SourceCitation } from "../stores/chatSession";
import { getHealthProfile } from "../utils/healthProfile";
import NotificationOptIn, { incrementChatCount } from "../components/NotificationOptIn";
import { memo } from "react";

// Hoisted outside component to avoid re-creating on every render/streaming chunk

function getSuggestedPrompts(
  conditions: string[],
  goals: string[],
  repStatus: string | null,
  isAuthenticated: boolean,
): string[] {
  const day = Math.floor(Date.now() / 86400000); // rotation seed

  const pick = <T,>(arr: T[]): T => arr[day % arr.length];
  const pick2 = <T,>(arr: T[], offset: number): T => arr[(day + offset) % arr.length];

  const tracking = [
    'Log how I\'m feeling today',
    'Track a symptom for me',
    'I want to log my period',
    'Record my mood today',
  ];
  const library = [
    'Explore the research library',
    'What topics can I learn about?',
  ];
  const capability = [
    'Help me prepare for a doctor visit',
    'Analyze my health patterns',
    'Generate an advocacy letter for my doctor',
  ];

  const repPrompts: Record<string, string> = {
    trying_to_conceive: 'Tips for optimizing fertility this cycle',
    pregnant: 'What should I know at this stage of pregnancy?',
    postpartum: 'Help me track my postpartum recovery',
    perimenopause: 'Managing my perimenopause symptoms',
    menopause: 'Latest research on menopause management',
  };

  if (!isAuthenticated) {
    return [
      'What can you help me with?',
      pick(tracking),
      pick(library),
      pick2(capability, 1),
    ];
  }

  if (conditions.length > 0) {
    const c = pick(conditions);
    const conditionPrompts = [
      `Latest research on ${c}`,
      `What treatment options exist for ${c}?`,
      `Tips for managing ${c} day-to-day`,
    ];
    const lastSlot = repStatus && repPrompts[repStatus]
      ? repPrompts[repStatus]
      : goals.length > 0
        ? 'Check in on my goals'
        : pick2(capability, 2);
    return [
      pick(conditionPrompts),
      pick(tracking),
      pick2(capability, 1),
      lastSlot,
    ];
  }

  if (goals.length > 0) {
    return [
      'Check in on my goals',
      pick(tracking),
      pick(library),
      pick2(capability, 1),
    ];
  }

  // Signed in, no profile data
  return [
    pick(tracking),
    pick(library),
    pick2(capability, 1),
    'What can you help me with?',
  ];
}

// Memoized message item — prevents re-rendering all messages on every streaming chunk
const ChatMessageItem = memo(function ChatMessageItem({
  message,
  onNavigate,
  onGenerateArtifact,
  onDeepResearch,
  onProfileUpdateStatus,
  onTrackingProtocolStatus,
  onProposedRecordStatus,
  onProposedRecordEdit,
  onLoginClick,
  isLastAssistant,
  isStreaming,
  chatId,
  onRegenerate,
  onFeedback,
  onRemoveFeedback,
  onSelectReply,
}: {
  message: Message;
  onNavigate: (path: string) => void;
  onGenerateArtifact?: (messageId: string, artifactType: string) => Promise<void>;
  onDeepResearch?: (messageId: string, query: string, focusArea: string) => Promise<void>;
  onProfileUpdateStatus?: (messageId: string, status: 'applied' | 'dismissed') => void;
  onTrackingProtocolStatus?: (messageId: string, status: 'applied' | 'dismissed') => void;
  onProposedRecordStatus?: (messageId: string, recordId: string, status: import("@researcher/shared").ProposedRecordStatus) => void;
  onProposedRecordEdit?: (messageId: string, recordId: string, data: import("@researcher/shared").ProposedLabRecord) => void;
  onLoginClick?: () => void;
  isLastAssistant?: boolean;
  isStreaming?: boolean;
  chatId?: string | null;
  onRegenerate?: () => void;
  onFeedback?: (messageId: string, value: 'up' | 'down', reasons?: string[], text?: string) => void;
  onRemoveFeedback?: (messageId: string) => void;
  onSelectReply?: (reply: string) => void;
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isResearching, setIsResearching] = useState(false);

  const handleArtifactClick = async () => {
    if (!message.artifactSuggest || !onGenerateArtifact || isGenerating) return;
    setIsGenerating(true);
    try {
      await onGenerateArtifact(message.id, message.artifactSuggest.type);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleResearchClick = async () => {
    if (!message.researchSuggest || !onDeepResearch || isResearching) return;
    setIsResearching(true);
    try {
      await onDeepResearch(message.id, message.researchSuggest.query, message.researchSuggest.focusArea);
    } finally {
      setIsResearching(false);
    }
  };
  if (message.role === 'assistant') {
    return (
      <AssistantMessage data-message-id={message.id} data-message-role="assistant">
          <div className="text-base leading-relaxed prose prose-base max-w-none prose-headings:text-gray-800 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-ul:text-gray-800 dark:prose-ul:text-gray-100 prose-ol:text-gray-800 dark:prose-ol:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100 prose-a:text-pink-600 dark:prose-a:text-pink-400 prose-a:underline hover:prose-a:text-pink-700">
            {isLastAssistant && isStreaming ? (
              <StreamingText
                content={sanitizeAssistantText(message.content)}
                isStreaming={!!isStreaming}
                components={MARKDOWN_COMPONENTS}
              />
            ) : (
              <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                {sanitizeAssistantText(message.content)}
              </ReactMarkdown>
            )}
            {message.artifact && <ArtifactCard artifact={message.artifact} artifactId={message.artifactId} />}
            {message.researchCard && <ResearchCard result={message.researchCard} />}
            {message.communitySuggest && (
              <button
                onClick={() => onNavigate(
                  message.communityPostSuggest
                    ? `/community/post/${message.communityPostSuggest.postId}`
                    : `/community/${message.communitySuggest}`
                )}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:from-orange-100 hover:to-pink-100 dark:hover:from-orange-900/30 dark:hover:to-pink-900/30 transition-colors group"
              >
                <UsersThree size={18} weight="fill" className="text-orange-500 flex-shrink-0" />
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                    {message.communityPostSuggest
                      ? message.communityPostSuggest.postTitle
                      : 'Others are discussing this in the community'}
                  </p>
                </div>
                <ArrowRight size={14} className="text-gray-400 group-hover:text-orange-500 flex-shrink-0 transition-colors" />
              </button>
            )}
            {message.profileUpdate && (
              <ProfileUpdateCard
                event={message.profileUpdate}
                savedStatus={message.profileUpdateStatus}
                onStatusChange={(s) => onProfileUpdateStatus?.(message.id, s)}
              />
            )}
            {message.trackingProtocolProposal && (
              <TrackingProtocolProposalCard
                event={message.trackingProtocolProposal}
                savedStatus={message.trackingProtocolStatus}
                onStatusChange={(s) => onTrackingProtocolStatus?.(message.id, s)}
              />
            )}
            {message.proposedRecords && message.proposedRecords.records.length > 0 && (
              <ProposedRecordsCard
                summary={message.proposedRecords.summary}
                sourceDescription={message.proposedRecords.sourceDescription}
                records={
                  // Merge any per-record edits the user made back onto the records
                  message.proposedRecordEdits
                    ? message.proposedRecords.records.map((r) => {
                        const edit = message.proposedRecordEdits?.[r.id];
                        return edit ? { ...r, data: edit } : r;
                      })
                    : message.proposedRecords.records
                }
                statuses={message.proposedRecordStatuses}
                onStatusChange={(rid, st) => onProposedRecordStatus?.(message.id, rid, st)}
                onDataChange={(rid, data) => onProposedRecordEdit?.(message.id, rid, data)}
              />
            )}
            {message.healthAnalysis && (
              <HealthAnalysisCard analysis={message.healthAnalysis} />
            )}
            {message.mealPlan && <MealPlanCard plan={message.mealPlan} />}
            {message.goalPlan && (
              <GoalPlanCard
                goalId={message.goalPlan.goalId}
                goalDescription={message.goalPlan.goalDescription}
                plan={message.goalPlan.plan}
              />
            )}
            {message.reminderCreated && (
              <div className={`mt-3 px-3 py-2.5 rounded-lg text-sm ${message.reminderCreated.notificationsEnabled === false ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'}`}>
                <div className="flex items-center gap-2">
                  <Bell size={16} weight="fill" className={message.reminderCreated.notificationsEnabled === false ? 'text-amber-500 flex-shrink-0' : 'text-emerald-500 flex-shrink-0'} />
                  <span className={message.reminderCreated.notificationsEnabled === false ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}>
                    {message.reminderCreated.action === 'deleted'
                      ? <>Reminder removed: <strong>{message.reminderCreated.title}</strong></>
                      : message.reminderCreated.action === 'disabled'
                        ? <>Reminder turned off: <strong>{message.reminderCreated.title}</strong></>
                        : message.reminderCreated.action === 'enabled'
                          ? <>Reminder turned back on: <strong>{message.reminderCreated.title}</strong></>
                          : <>Reminder {message.reminderCreated.action === 'updated' ? 'updated' : 'set'}: <strong>{message.reminderCreated.title}</strong> — {message.reminderCreated.frequency} at {message.reminderCreated.time}
                            {message.reminderCreated.daysOfWeek?.length ? ` on ${message.reminderCreated.daysOfWeek.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join('/')}` : ''}</>
                    }
                  </span>
                </div>
                {message.reminderCreated.notificationsEnabled === false && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 ml-6">
                    Enable notifications in settings so this reminder can reach you.
                  </p>
                )}
              </div>
            )}
            {message.protocolEnrolled && (
              <div className="mt-3 px-3 py-2.5 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <ListChecks size={16} weight="fill" className="text-pink-500 flex-shrink-0" />
                  <span className="text-pink-700 dark:text-pink-300">
                    Daily check-in set up: <strong>{message.protocolEnrolled.conditionLabel}</strong> — {message.protocolEnrolled.schedule.frequency} at {message.protocolEnrolled.schedule.time}
                  </span>
                </div>
                <p className="text-xs text-pink-600 dark:text-pink-400 mt-1.5 ml-6">
                  {message.protocolEnrolled.items.length} items: {message.protocolEnrolled.items.join(', ')}
                </p>
              </div>
            )}
            {message.protocolUpdated && (() => {
              const action = message.protocolUpdated.action;
              const Icon = action === 'pause' ? Pause : action === 'resume' ? Play : CheckCircle;
              const label = action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'completed';
              return (
                <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
                  <Icon size={16} weight="fill" className="text-gray-500 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Tracking {label}
                  </span>
                </div>
              );
            })()}
            {message.preferenceSet && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg text-sm">
                <SlidersHorizontal size={16} weight="fill" className="text-violet-500 flex-shrink-0" />
                <span className="text-violet-700 dark:text-violet-300">
                  Preference {message.preferenceSet.action}: <strong>{message.preferenceSet.label}</strong>
                </span>
              </div>
            )}
            {message.trialSuggest && (
              <button
                onClick={() => onNavigate(`/trials?q=${encodeURIComponent(message.trialSuggest!)}`)}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:from-orange-100 hover:to-pink-100 dark:hover:from-orange-900/30 dark:hover:to-pink-900/30 transition-colors group"
              >
                <Heartbeat size={18} weight="fill" className="text-pink-500 flex-shrink-0" />
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Search clinical trials for {message.trialSuggest}</p>
                </div>
                <ArrowRight size={14} className="text-gray-400 group-hover:text-pink-500 flex-shrink-0 transition-colors" />
              </button>
            )}
            {message.artifactSuggest && !message.artifact && (
              <button
                onClick={handleArtifactClick}
                disabled={isGenerating}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:from-orange-100 hover:to-pink-100 dark:hover:from-orange-900/30 dark:hover:to-pink-900/30 transition-colors group disabled:opacity-60"
              >
                {isGenerating
                  ? <CircleNotch size={18} weight="bold" className="text-pink-500 flex-shrink-0 animate-spin" />
                  : <span className="text-pink-500 flex-shrink-0"><RomyIcon size={18} /></span>
                }
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                    {isGenerating ? 'Generating...' : message.artifactSuggest.label}
                  </p>
                </div>
                {!isGenerating && <ArrowRight size={14} className="text-gray-400 group-hover:text-pink-500 flex-shrink-0 transition-colors" />}
              </button>
            )}
            {message.researchSuggest && !message.researchCard && (
              <button
                onClick={handleResearchClick}
                disabled={isResearching}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2.5 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:from-orange-100 hover:to-pink-100 dark:hover:from-orange-900/30 dark:hover:to-pink-900/30 transition-colors group disabled:opacity-60"
              >
                {isResearching
                  ? <CircleNotch size={18} weight="bold" className="text-pink-500 flex-shrink-0 animate-spin" />
                  : <BookOpen size={18} weight="fill" className="text-pink-500 flex-shrink-0" />
                }
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                    {isResearching ? 'Pulling research sources...' : message.researchSuggest.label}
                  </p>
                </div>
                {!isResearching && <ArrowRight size={14} className="text-gray-400 group-hover:text-pink-500 flex-shrink-0 transition-colors" />}
              </button>
            )}
            {message.isNudge && onLoginClick && (
              <button
                onClick={onLoginClick}
                className="mt-3 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-sm"
              >
                Sign In
              </button>
            )}
            {message.sources && message.sources.length > 0 && (
              <SourcesFooter sources={message.sources} />
            )}
            {isLastAssistant && !isStreaming && message.suggestedReplies && message.suggestedReplies.length > 0 && onSelectReply && (
              <SuggestedReplies
                replies={message.suggestedReplies}
                onSelect={onSelectReply}
              />
            )}
          </div>
          {onRegenerate && onFeedback && (
            <MessageActions
              message={message}
              isLastAssistant={!!isLastAssistant}
              isStreaming={!!isStreaming}
              chatId={chatId ?? null}
              onRegenerate={onRegenerate}
              onFeedback={onFeedback}
              onRemoveFeedback={onRemoveFeedback}
            />
          )}
      </AssistantMessage>
    );
  }

  // Hidden system nudge — don't render as a user bubble
  if (message.isNudge && message.role === 'user') return null;

  // User message
  return (
    <UserMessage
      content={message.content}
      attachments={message.attachments}
      attachmentPreviewUrls={message.attachmentPreviewUrls}
      data-message-id={message.id}
      data-message-role="user"
    />
  );
});

export default function Chat() {
  const { currentUser, userProfile, onboardingCompleted, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const topic = searchParams.get("topic");
  const initialMessage = searchParams.get("message");
  const skipExtractionParam = searchParams.get("skipExtraction") === "1";
  const landingPreview = searchParams.get("preview");
  const prefill = searchParams.get("prefill");
  const prompt = searchParams.get("prompt");
  const postId = searchParams.get("postId");
  const postTitle = searchParams.get("postTitle");
  const deepLinkChatId = searchParams.get("chatId");
  const processedMessageRef = useRef<string | null>(null);
  const processedPromptRef = useRef<string | null>(null);
  const processedDeepLinkChatIdRef = useRef<string | null>(null);
  const processedPostIdRef = useRef<string | null>(null);
  // Track community post ID for inclusion in API request
  const communityPostIdRef = useRef<string | null>(null);

  // Persistent chat session (survives route navigation, clears on page refresh)
  const { messages, currentChatId, input, setMessages, setCurrentChatId, setInput, reset: resetChatSession } = useChatSession();

  // Chat history state
  const [chatHistory, setChatHistory] = useState<ChatConversation[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [healthRefreshTrigger, setHealthRefreshTrigger] = useState(0);
  const [healthToast, setHealthToast] = useState<HealthLogEntry | null>(null);
  const healthSnapshotRef = useRef<Map<string, number>>(new Map());
  const [appointmentToast, setAppointmentToast] = useState<{ title: string; date: string } | null>(null);
  const appointmentSnapshotRef = useRef<Map<string, number>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  // Tool-aware label for the "working…" indicator (e.g. "Building your meal
  // plan…"); falls back to a friendly generic if the server sends none.
  const [thinkingLabel, setThinkingLabel] = useState('Working on it…');
  const [appBannerDismissed, setAppBannerDismissed] = useState(false);
  // Intake nudge: empty-profile users get an opt-in express lane to set up their
  // profile. Shares the Health page's dismiss flag so dismissing once kills both.
  const [profileEmpty, setProfileEmpty] = useState(false);
  const [intakeDismissed, setIntakeDismissed] = useState(() => {
    try { return localStorage.getItem('romy_intake_dismissed') === 'true'; } catch { return false; }
  });
  const keyboardHeight = useKeyboardHeight();
  const unauthMsgCountRef = useRef(0);
  // showProfilePrompt removed — conversational onboarding replaces it
  const [userConditions, setUserConditions] = useState<string[]>([]);
  const [userGoalDescs, setUserGoalDescs] = useState<string[]>([]);
  const [reproductiveStatus, setReproductiveStatus] = useState<string | null>(null);

  // Contextual opening from Romy
  const [openingMessage, setOpeningMessage] = useState<string | null>(null);
  const [openingChips, setOpeningChips] = useState<string[]>([]);
  const [openingMetadata, setOpeningMetadata] = useState<Record<string, string> | null>(null);
  const [openingLoaded, setOpeningLoaded] = useState(false);
  const [deleteChatId, setDeleteChatId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [retentionNotice, setRetentionNotice] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [showAIConsent, setShowAIConsent] = useState(false);
  const [aiConsented, setAiConsented] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastMessageCountRef = useRef<number>(0);

  const scrollToNewMessage = () => {
    // When a new message arrives, smoothly scroll the user's question to the
    // top of the chat viewport — like Gemini — so the full screen is available
    // for the incoming assistant response.
    if (messagesContainerRef.current && messages.length > lastMessageCountRef.current) {
      const messageElements = messagesContainerRef.current.querySelectorAll('[data-message-id]');
      if (messageElements.length > 0) {
        // Find the latest user message (the question that was just sent)
        let targetEl: HTMLElement | null = null;
        for (let i = messageElements.length - 1; i >= 0; i--) {
          const el = messageElements[i] as HTMLElement;
          // User messages come first, then assistant — scroll to the user msg
          if (el.getAttribute('data-message-role') === 'user') {
            targetEl = el;
            break;
          }
        }
        // Fall back to last message if no user msg found
        if (!targetEl) {
          targetEl = messageElements[messageElements.length - 1] as HTMLElement;
        }
        // Compute target position via getBoundingClientRect — robust against
        // offsetParent nesting (the previous offsetTop math drifted when the
        // container wasn't the message's offsetParent, which left the user
        // message "part way up" instead of at the top).
        const container = messagesContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const targetTop = targetRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({ top: Math.max(0, targetTop - 16), behavior: "smooth" });
      }
      lastMessageCountRef.current = messages.length;
    }
  };

  // Scroll when a new message is added (user sends or assistant message appears).
  // Only scrolls forward (to new content), never backward.
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      requestAnimationFrame(() => scrollToNewMessage());
    }
  }, [messages.length]);

  // Auto-focus input on mount (desktop only — mobile pops keyboard)
  useEffect(() => {
    if (window.innerWidth >= 768) {
      inputRef.current?.focus();
    }
  }, []);

  // Load health profile for personalized suggested prompts
  useEffect(() => {
    if (!currentUser) return;
    getHealthProfile()
      .then(({ healthProfile, healthGoals }) => {
        if (healthProfile?.conditions?.length) {
          setUserConditions(healthProfile.conditions.map(c => c.name));
        }
        if (healthGoals?.length) {
          setUserGoalDescs(healthGoals.filter(g => g.status === 'active').map(g => g.description));
        }
        if (healthProfile?.reproductiveStatus) {
          setReproductiveStatus(healthProfile.reproductiveStatus);
        }
        setProfileEmpty(
          !healthProfile ||
          (!healthProfile.conditions?.length && !healthProfile.ageRange && !healthProfile.dateOfBirth && !healthProfile.reproductiveStatus)
        );
      })
      .catch(() => {});
  }, [currentUser]);

  // Fetch contextual opening from Romy
  useEffect(() => {
    if (!currentUser || openingLoaded) return;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    currentUser.getIdToken().then(token => {
      headers['Authorization'] = `Bearer ${token}`;
      fetch(apiUrl('/chat/opening'), { headers })
        .then(res => {
          if (res.status === 204) return null;
          return res.json();
        })
        .then(data => {
          if (data?.data) {
            setOpeningMessage(data.data.message);
            setOpeningChips(data.data.chips || []);
            if (data.data.metadata) setOpeningMetadata(data.data.metadata);
          }
        })
        .catch(() => {})
        .finally(() => setOpeningLoaded(true));
    });
  }, [currentUser, openingLoaded]);

  // Load chat history
  const loadChatHistory = useCallback(async () => {
    if (!currentUser) return;

    setIsLoadingChats(true);
    try {
      const chats = await getUserChats(currentUser.uid);
      setChatHistory(chats);
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setIsLoadingChats(false);
    }
  }, [currentUser]);

  // Single-flight chat creation. Every path that might need to create the chat
  // doc (send handler, debounced save, unmount flush, deep-links) routes through
  // here, so concurrent callers can never spawn duplicate chat documents — the
  // root cause of the "duplicate truncated chats" bug. Returns the existing id
  // immediately if one is set, or the in-flight creation promise if one is
  // already running.
  const creatingChatRef = useRef<Promise<string> | null>(null);
  const ensureChatId = useCallback(async (seed?: Message): Promise<string | null> => {
    if (!currentUser) return null;
    const existing = useChatSession.getState().currentChatId;
    if (existing) return existing;
    if (creatingChatRef.current) return creatingChatRef.current;
    const p = (async () => {
      const chatId = await createChat(currentUser.uid, seed as ChatMessageType | undefined);
      setCurrentChatId(chatId);
      loadChatHistory().catch(() => {});
      return chatId;
    })();
    creatingChatRef.current = p;
    try {
      return await p;
    } finally {
      creatingChatRef.current = null;
    }
  }, [currentUser, setCurrentChatId, loadChatHistory]);

  // Persist the live conversation to its chat doc, creating the doc if needed.
  // Reads straight from the store (NOT stale closures) so it always targets the
  // correct chat with the full, current message list — including card payloads
  // (meal plans, sources, research, …) that the old per-turn rebuild dropped.
  const persistChat = useCallback(async () => {
    if (!currentUser) return;
    const liveMsgs = useChatSession.getState().messages;
    // Don't create a chat for an opening-only greeting — wait for a real user turn.
    const seed = liveMsgs.find(m => m.role === 'user' && !m.isNudge);
    if (!seed) return;
    try {
      const chatId = await ensureChatId(seed);
      if (!chatId) return;
      await updateChat(chatId, useChatSession.getState().messages as ChatMessageType[]);
    } catch (error) {
      console.error('Error saving chat:', error);
    }
  }, [currentUser, ensureChatId]);

  // Track previous user to detect sign-out transitions
  const prevUserRef = useRef(currentUser);
  // Track whether we've already loaded history for this user (prevents re-loading on every messages change)
  const historyLoadedForUserRef = useRef<string | null>(null);

  // Load chat history when user signs in; clear on sign-out. Deliberately does
  // NOT create chats here — persistChat (debounced save + end-of-turn) handles
  // the logged-out→sign-in case via single-flight ensureChatId. Keeping this
  // effect off `messages`/`currentChatId` also stops it re-running every turn.
  useEffect(() => {
    if (currentUser) {
      // Only load history once per user session — not on every messages/streaming change
      if (historyLoadedForUserRef.current !== currentUser.uid) {
        historyLoadedForUserRef.current = currentUser.uid;
        loadChatHistory().catch(error => {
          console.error('Failed to load chat history:', error);
        });
      }
      prevUserRef.current = currentUser;
    } else if (prevUserRef.current) {
      // Only clear on actual sign-out (had a user before), not while
      // unauthenticated users are chatting
      historyLoadedForUserRef.current = null;
      prevUserRef.current = null;
      setChatHistory([]);
      resetChatSession();
    }
  }, [currentUser, loadChatHistory, resetChatSession]);

  // Debounced background save on message changes. Skipped while streaming to
  // avoid excessive writes / sidebar flashing. persistChat creates the chat doc
  // if needed and reads live store state, so it always saves the full convo.
  useEffect(() => {
    if (isStreaming || !currentUser) return;
    const timeoutId = setTimeout(() => { persistChat(); }, 1000);
    return () => clearTimeout(timeoutId);
  }, [messages, currentUser, isStreaming, persistChat]);

  // Flush the latest conversation on unmount. Navigating away within the 1s
  // debounce window used to CANCEL the pending save (clearTimeout only), losing
  // the most recent turn — the core of the "chat truncates on navigate-away"
  // bug. Unmount-only (empty deps); the latest persistChat is held in a ref and
  // reads live store state, which survives the unmount.
  const persistRef = useRef(persistChat);
  persistRef.current = persistChat;
  useEffect(() => {
    return () => { persistRef.current(); };
  }, []);

  // Load a specific chat
  const loadChat = async (chatId: string) => {
    if (!currentUser) return;

    // Save current chat before switching (if it has content)
    await persistChat();

    setIsLoading(true);
    try {
      const chat = await getChat(chatId);
      if (chat) {
        setMessages(chat.messages as Message[]);
        setCurrentChatId(chatId);
        setIsSidebarOpen(false); // Close sidebar on mobile after selecting
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Start a new chat
  const startNewChat = () => {
    resetChatSession();
    setAppBannerDismissed(false);
    setIsSidebarOpen(false); // Close sidebar on mobile
    inputRef.current?.focus();
  };

  // Delete a chat
  const handleDeleteChat = (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent loading the chat when clicking delete
    setDeleteChatId(chatId);
  };

  const confirmDeleteChat = async () => {
    if (!deleteChatId) return;
    try {
      await deleteChat(deleteChatId);
      if (currentChatId === deleteChatId) {
        startNewChat();
      }
      await loadChatHistory();
    } catch (error) {
      console.error('Error deleting chat:', error);
      reportError(error, 'data_save');
      setDeleteError("Couldn't delete that chat — try again in a moment.");
    }
    setDeleteChatId(null);
  };

  const handleBuildPacket = (_chatId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    navigate('/health/appointments?new=true');
  };


  // Check if user has consented to AI data sharing
  const hasAIConsent = aiConsented || userProfile?.aiDataConsent?.consented;

  const handleAIConsent = useCallback(async () => {
    if (currentUser) {
      try {
        await setDoc(doc(db, 'users', currentUser.uid), {
          aiDataConsent: {
            consented: true,
            consentedAt: serverTimestamp(),
            consentVersion: '1.0',
          },
        }, { merge: true });
      } catch (e) {
        reportError(e as Error, 'data_save');
      }
    }
    setAiConsented(true);
    setShowAIConsent(false);
    // Send the pending message if there is one
    if (pendingMessageRef.current) {
      const msg = pendingMessageRef.current;
      pendingMessageRef.current = null;
      // Defer so state updates are flushed
      setTimeout(() => handleSendMessageInner(msg), 0);
    }
  }, [currentUser]);

  const handleAIConsentDecline = useCallback(() => {
    setShowAIConsent(false);
    pendingMessageRef.current = null;
  }, []);

  // Extract send logic into a reusable function
  const handleSendMessageInner = useCallback(async (
    messageText: string,
    opts?: {
      skipExtraction?: boolean;
      isNudge?: boolean;
      attachments?: ChatAttachment[];
      attachmentPreviewUrls?: Record<string, string>;
    },
  ) => {
    const hasAttachments = (opts?.attachments?.length ?? 0) > 0;
    if ((!messageText.trim() && !hasAttachments) || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: messageText.trim(),
      timestamp: new Date(),
      ...(opts?.isNudge && { isNudge: true }),
      ...(opts?.attachments && opts.attachments.length > 0 && {
        attachments: opts.attachments,
        attachmentPreviewUrls: opts.attachmentPreviewUrls,
      }),
    };

    setIsLoading(true);

    // Snapshot recent health logs and appointments so we can detect new extractions later
    if (currentUser) {
      try {
        const recent = await getHealthLogs({ limit: 5 });
        healthSnapshotRef.current = new Map(
          recent.items.map(i => [i.id, i.updatedAt instanceof Date ? i.updatedAt.getTime() : new Date(i.updatedAt).getTime()])
        );
      } catch { /* ignore */ }
      try {
        const upcomingAppts = await getAppointments('upcoming');
        appointmentSnapshotRef.current = new Map(
          upcomingAppts.map((a: Appointment) => [a.id, a.updatedAt instanceof Date ? a.updatedAt.getTime() : new Date(a.updatedAt).getTime()])
        );
      } catch { /* ignore */ }
    }

    // Ensure a chat doc exists so it shows in the sidebar immediately. Single-
    // flight + no-op if one already exists; the end-of-turn persistChat will
    // also ensure/save, so we don't block streaming on this.
    if (currentUser) {
      ensureChatId(userMessage).catch(() => {});
    }

    // Get current messages using functional update.
    // Include card/tool metadata so Gemini knows what it already proposed.
    let conversationHistoryForAPI: Array<{ role: string; content: string }> = [];
    setMessages((prev) => {
      // If this is the first message and we showed an opening greeting,
      // inject it as conversation context so Romy knows what she said, AND
      // render it as a real assistant message so the user can see what they
      // were responding to (otherwise tapping a chip leaves a context-free
      // "Let's do it" floating in chat).
      if (prev.length === 0 && openingMessage) {
        let openingContent = openingMessage;
        if (openingMetadata?.goalId) {
          openingContent += `\n[context: this message is about goal_id=${openingMetadata.goalId}]`;
        }
        conversationHistoryForAPI = [
          { role: 'assistant', content: openingContent },
        ];
        const openingAssistantMessage: Message = {
          id: `opening_${Date.now()}`,
          role: 'assistant',
          content: openingMessage,
          timestamp: new Date(),
        };
        return [...prev, openingAssistantMessage, userMessage];
      }
      conversationHistoryForAPI = prev.map((m) => {
        let content = m.content;
        if (m.role === 'assistant') {
          const meta: string[] = [];
          if (m.profileUpdate) {
            const status = m.profileUpdateStatus || 'pending';
            meta.push(`[Profile update proposed: ${m.profileUpdate.summary} — ${status}]`);
          }
          if (m.artifact) {
            meta.push(`[Artifact generated: ${m.artifact.type || 'document'}]`);
          }
          if (m.artifactSuggest && !m.artifact) {
            meta.push(`[Artifact suggested: ${m.artifactSuggest.label}]`);
          }
          if (m.communitySuggest) {
            meta.push(`[Community topic suggested: ${m.communitySuggest}]`);
          }
          if (m.trialSuggest) {
            meta.push(`[Clinical trials suggested for: ${m.trialSuggest}]`);
          }
          if (m.goalPlan) {
            meta.push(`[Goal plan created: ${m.goalPlan.goalDescription}]`);
          }
          if (m.mealPlan) {
            meta.push(`[Meal plan created: "${m.mealPlan.title}" (id: ${m.mealPlan.id})]`);
          }
          if (m.reminderCreated) {
            meta.push(`[Reminder set: ${m.reminderCreated.title} (${m.reminderCreated.frequency} at ${m.reminderCreated.time})]`);
          }
          if (m.protocolEnrolled) {
            meta.push(`[Daily check-in set up: ${m.protocolEnrolled.conditionLabel} (${m.protocolEnrolled.items.length} items, ${m.protocolEnrolled.schedule.frequency} at ${m.protocolEnrolled.schedule.time})]`);
          }
          if (m.trackingProtocolProposal) {
            const status = m.trackingProtocolStatus || 'pending';
            meta.push(`[Tracking protocol proposed: ${m.trackingProtocolProposal.conditionLabel} (${m.trackingProtocolProposal.frequency} at ${m.trackingProtocolProposal.time}) — ${status}]`);
          }
          if (m.protocolUpdated) {
            meta.push(`[Daily check-in ${m.protocolUpdated.action}]`);
          }
          if (m.preferenceSet) {
            meta.push(`[Preference ${m.preferenceSet.action}: ${m.preferenceSet.label}]`);
          }
          if (meta.length > 0) {
            content += '\n' + meta.join('\n');
          }
        }
        return { role: m.role, content };
      });
      return [...prev, userMessage];
    });
    setInput("");

    try {
      const token = currentUser ? await currentUser.getIdToken() : null;

      const headers: HeadersInit = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      // Include communityPostId if this message was triggered by "Ask Romy" on a post
      // Timestamp of the last message before this turn, so the server can tell
      // Romy when a conversation is being resumed after a long gap (she never
      // sees per-message timestamps otherwise). `messages` here is the
      // pre-append state — the new user message was added via a functional
      // setMessages update and isn't reflected in this closure yet.
      const lastPriorMessage = messages[messages.length - 1];
      const lastMessageAt = lastPriorMessage
        ? (lastPriorMessage.timestamp instanceof Date
            ? lastPriorMessage.timestamp
            : new Date(lastPriorMessage.timestamp)
          ).toISOString()
        : undefined;

      const requestBody: any = {
        message: userMessage.content,
        conversationHistory: conversationHistoryForAPI,
        ...(opts?.skipExtraction && { skipExtraction: true }),
        ...(currentChatId && { chatId: currentChatId }),
        ...(opts?.attachments && opts.attachments.length > 0 && { attachments: opts.attachments }),
        ...(lastMessageAt && { lastMessageAt }),
        detectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (communityPostIdRef.current) {
        requestBody.communityPostId = communityPostIdRef.current;
        communityPostIdRef.current = null; // Only send once
      }

      const response = await fetch(apiUrl('/chat'), {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      // Handle different error status codes
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Email verification required (403)
        if (response.status === 403 && errorData.requiresVerification) {
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: `Please verify your email address to use the chat feature. We've sent a verification email to ${errorData.email || 'your email'}. Please check your inbox and click the verification link, then try again.`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        // Rate limit exceeded (429)
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 900; // seconds
          const minutes = Math.ceil(retryAfter / 60);
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: `You've sent too many messages. Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before trying again.${!currentUser ? ' Sign in for higher limits!' : ''}`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }

        // Validation errors (400) or other errors
        reportError(new Error(`Chat HTTP ${response.status}`), 'chat');
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "I'm having a little trouble right now. Try sending that again in a moment.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);
        return;
      }

      // Stream SSE response
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      const assistantMessageId = (Date.now() + 1).toString();
      let fullContent = '';
      let messageAdded = false;
      let buffer = '';
      let pendingArtifact: ArtifactContent | undefined;
      let pendingSources: SourceCitation[] | undefined;
      let pendingSuggestedReplies: string[] | undefined;

      // Ensure the assistant message exists in the messages array.
      // Must be called before any setMessages map-update so the message can be found.
      // Note: this does NOT flip isStreaming — that happens only on the first
      // text chunk. Sources / suggestedReplies / tool events can create the
      // message bubble before any text exists; if those events also flipped
      // isStreaming, the loading dots (gated on `isLoading && !isStreaming`)
      // would vanish before the user has anything to read.
      const ensureMessage = () => {
        if (!messageAdded) {
          setMessages((prev) => [...prev, {
            id: assistantMessageId,
            role: 'assistant' as const,
            content: '',
            timestamp: new Date(),
          }]);
          messageAdded = true;
        }
      };

      // Stall watchdog: if the server stops sending data for this long, stop
      // waiting and finalize gracefully instead of leaving the "Analyzing…"
      // indicator spinning forever. Generous enough to cover slow server-side
      // tool synthesis (artifact/goal-plan generation runs up to ~60s with no
      // intervening SSE events) while still bounding a genuinely hung stream.
      const STALL_TIMEOUT_MS = 75_000;
      const STALL = Symbol('stall');

      while (true) {
        const readResult = await Promise.race([
          reader.read(),
          new Promise<typeof STALL>((resolve) =>
            setTimeout(() => resolve(STALL), STALL_TIMEOUT_MS),
          ),
        ]);
        if (readResult === STALL) {
          // Release the reader and fall through to the normal finalize path,
          // which clears the thinking/streaming flags and applies the
          // suggested-reply heuristic so chips still render.
          reader.cancel().catch(() => {});
          break;
        }
        const { done, value } = readResult;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.thinking) {
              setThinkingLabel(typeof parsed.label === 'string' && parsed.label ? parsed.label : 'Working on it…');
              setIsThinking(true);
            }
            if (parsed.chunk) {
              setIsThinking(false);
              fullContent += parsed.chunk;
              ensureMessage();
              setIsStreaming(true);
              // Strip citation markers, leaked internal tags, and normalize <br>
              const updatedContent = sanitizeAssistantText(fullContent);
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, content: updatedContent }
                  : m
              ));
            }
            if (parsed.artifact) {
              // Attach structured artifact to the assistant message
              pendingArtifact = parsed.artifact as ArtifactContent;
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, artifact: parsed.artifact, artifactId: parsed.artifactId }
                  : m
              ));
            }
            if (parsed.proposedRecords) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, proposedRecords: parsed.proposedRecords }
                  : m
              ));
            }
            if (parsed.communitySuggest) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, communitySuggest: parsed.communitySuggest }
                  : m
              ));
            }
            if (parsed.communityPostSuggest) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, communityPostSuggest: parsed.communityPostSuggest }
                  : m
              ));
            }
            if (parsed.profileUpdate) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, profileUpdate: parsed.profileUpdate }
                  : m
              ));
            }
            if (parsed.trialSuggest) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, trialSuggest: parsed.trialSuggest }
                  : m
              ));
            }
            if (parsed.artifactSuggest) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, artifactSuggest: parsed.artifactSuggest }
                  : m
              ));
            }
            if (parsed.researchSuggest) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, researchSuggest: parsed.researchSuggest }
                  : m
              ));
            }
            if (parsed.healthAnalysis) {
              ensureMessage();
              setMessages((prev) => {
                const incoming = parsed.healthAnalysis;
                // Dedup the empty "no patterns yet" chip: if this thread already
                // showed an empty analysis for the same symptom, don't repeat it.
                // (Non-empty analyses always render — they carry new findings.)
                if (incoming?.insightCount === 0) {
                  const symptom = (incoming.symptom || '').toLowerCase();
                  const alreadyShown = prev.some((m) =>
                    m.id !== assistantMessageId &&
                    m.healthAnalysis?.insightCount === 0 &&
                    (m.healthAnalysis.symptom || '').toLowerCase() === symptom
                  );
                  if (alreadyShown) return prev;
                }
                return prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, healthAnalysis: incoming }
                    : m
                );
              });
            }
            if (parsed.researchCard) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, researchCard: parsed.researchCard }
                  : m
              ));
            }
            if (parsed.goalPlan) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, goalPlan: parsed.goalPlan }
                  : m
              ));
            }
            if (parsed.mealPlan) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, mealPlan: parsed.mealPlan }
                  : m
              ));
            }
            if (parsed.reminderCreated) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, reminderCreated: parsed.reminderCreated }
                  : m
              ));
            }
            if (parsed.protocolEnrolled) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, protocolEnrolled: parsed.protocolEnrolled }
                  : m
              ));
            }
            if (parsed.trackingProtocolProposal) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, trackingProtocolProposal: parsed.trackingProtocolProposal }
                  : m
              ));
            }
            if (parsed.protocolUpdated) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, protocolUpdated: parsed.protocolUpdated }
                  : m
              ));
            }
            if (parsed.preferenceSet) {
              ensureMessage();
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, preferenceSet: parsed.preferenceSet }
                  : m
              ));
            }
            if (parsed.sources) {
              ensureMessage();
              pendingSources = parsed.sources;
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, sources: parsed.sources }
                  : m
              ));
            }
            if (parsed.suggestedReplies && Array.isArray(parsed.suggestedReplies)) {
              ensureMessage();
              pendingSuggestedReplies = parsed.suggestedReplies;
              setMessages((prev) => prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, suggestedReplies: parsed.suggestedReplies }
                  : m
              ));
            }
          } catch (parseError: any) {
            if (parseError.message && parseError.message !== data) {
              // Re-throw actual errors from the server
              throw parseError;
            }
          }
        }
      }

      setIsStreaming(false);
      setIsThinking(false);

      // Build final assistant message for persistence
      // Heuristic fallback for suggested replies — only fires when the
      // tool didn't already provide them.
      const finalSuggestedReplies = pendingSuggestedReplies
        ?? inferSuggestedReplies(fullContent)
        ?? undefined;

      if (finalSuggestedReplies && !pendingSuggestedReplies) {
        // Heuristic chose them — push to the in-memory message too so they render.
        setMessages((prev) => prev.map((m) =>
          m.id === assistantMessageId
            ? { ...m, suggestedReplies: finalSuggestedReplies }
            : m
        ));
      }

      const assistantMessage: Message = {
        id: assistantMessageId,
        role: "assistant",
        content: sanitizeAssistantText(fullContent) || "I wasn't able to put a response together — try asking again.",
        timestamp: new Date(),
        ...(pendingArtifact && { artifact: pendingArtifact }),
        ...(pendingSources && pendingSources.length > 0 && { sources: pendingSources }),
        ...(finalSuggestedReplies && finalSuggestedReplies.length > 0 && { suggestedReplies: finalSuggestedReplies }),
      };

      // If no chunks arrived (empty response), add the message now
      if (!messageAdded) {
        setMessages((prev) => [...prev, assistantMessage]);
      }

      // Track chat count for notification opt-in prompt
      incrementChatCount();

      // Soft sign-up nudge for unauthenticated users after 5 messages
      if (!currentUser) {
        unauthMsgCountRef.current += 1;
        if (unauthMsgCountRef.current === 5) {
          const nudgeMessage: Message = {
            id: `nudge-${Date.now()}`,
            role: 'assistant',
            content: "I'm really enjoying our conversation! If you sign in, I can save our chat history and personalize my responses to your health profile. It only takes a moment.",
            timestamp: new Date(),
            isNudge: true,
          };
          setMessages((prev) => [...prev, nudgeMessage]);
        }
      }

      // Check for new or updated health data after background extraction completes
      // Poll a few times since Gemini extraction can take variable time
      if (currentUser) {
        const pollForExtraction = async (attempt: number) => {
          try {
            const recent = await getHealthLogs({ limit: 5 });
            const changedEntry = recent.items.find((e) => {
              if (e.source !== 'auto_extract') return false;
              const prevUpdatedAt = healthSnapshotRef.current.get(e.id);
              if (prevUpdatedAt === undefined) return true; // New entry
              const currentUpdatedAt = e.updatedAt instanceof Date ? e.updatedAt.getTime() : new Date(e.updatedAt).getTime();
              return currentUpdatedAt > prevUpdatedAt; // Updated entry
            });
            if (changedEntry) {
              const ts = changedEntry.updatedAt instanceof Date ? changedEntry.updatedAt.getTime() : new Date(changedEntry.updatedAt).getTime();
              healthSnapshotRef.current.set(changedEntry.id, ts);
              setHealthToast(changedEntry);
              setHealthRefreshTrigger((n) => n + 1);
            }
          } catch { /* ignore */ }

          // Also check for new auto-extracted appointments
          try {
            const upcomingAppts = await getAppointments('upcoming');
            const newAppt = upcomingAppts.find((a: Appointment) => {
              if (a.source !== 'auto_extract') return false;
              return !appointmentSnapshotRef.current.has(a.id);
            });
            if (newAppt) {
              appointmentSnapshotRef.current.set(newAppt.id, newAppt.updatedAt instanceof Date ? newAppt.updatedAt.getTime() : new Date(newAppt.updatedAt).getTime());
              setAppointmentToast({ title: newAppt.title, date: newAppt.date });
              return;
            }
          } catch { /* ignore */ }

          // Retry up to 3 times (at 4s, 8s, 14s)
          if (attempt < 3) {
            setTimeout(() => pollForExtraction(attempt + 1), attempt === 1 ? 4000 : 6000);
          }
        };
        setTimeout(() => pollForExtraction(1), 4000);
      }

      // Persist the completed turn from live store state. This captures the
      // streamed assistant message WITH any card payloads (meal plan, sources,
      // research, …) and creates the chat doc if this was the first turn —
      // fixing both the dropped-card bug and the skipped-turn-1-save bug.
      if (currentUser) {
        await persistChat();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // model_overloaded = upstream model throttled us (429/503) even after
      // in-app retries. Expected operational blip, not a bug — give the user a
      // retry-friendly message and don't report it as an error.
      const overloaded = error instanceof Error && error.message === 'model_overloaded';
      if (!overloaded) reportError(error, 'chat');
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: overloaded
          ? "I'm getting a flood of requests right now and couldn't get to yours. Give it a few seconds and send it again — it usually clears up fast."
          : "I'm having trouble connecting right now. Give it another try in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setIsThinking(false);
      inputRef.current?.focus();

      // Auto-complete onboarding after first assistant response
      if (!onboardingCompleted) {
        completeOnboarding().catch(() => {});
      }
    }
  }, [currentUser, isLoading, currentChatId, messages, onboardingCompleted, completeOnboarding, openingMessage, openingMetadata, ensureChatId, persistChat]);

  // Gate message sending behind AI data consent for authenticated users
  const handleSendMessage = useCallback(async (
    messageText: string,
    opts?: {
      skipExtraction?: boolean;
      attachments?: ChatAttachment[];
      attachmentPreviewUrls?: Record<string, string>;
    },
  ) => {
    if (currentUser && !hasAIConsent) {
      pendingMessageRef.current = messageText;
      setShowAIConsent(true);
      return;
    }
    return handleSendMessageInner(messageText, opts);
  }, [currentUser, hasAIConsent, handleSendMessageInner]);

  // Persist profile update card status on the message so it survives remounts.
  // For most proposal types the card is the receipt (Romy already asked her
  // follow-up question before it). The exception is add_health_goal, which
  // needs a follow-up turn so Romy can gather preferences and call
  // create_goal_plan — see GOAL FLOWS in the system prompt.
  const handleProfileUpdateStatus = useCallback((messageId: string, status: 'applied' | 'dismissed') => {
    let acceptedGoal = false;
    setMessages((prev) => prev.map((m) => {
      if (m.id !== messageId) return m;
      if (status === 'applied' && m.profileUpdate?.proposals.some(p => p.action === 'add_health_goal')) {
        acceptedGoal = true;
      }
      return { ...m, profileUpdateStatus: status };
    }));

    if (status === 'applied') {
      setHealthRefreshTrigger((n) => n + 1);

      if (acceptedGoal && !isLoading) {
        setTimeout(() => {
          handleSendMessageInner('[User accepted the health goal]', { skipExtraction: true, isNudge: true });
        }, 600);
      }
    }
  }, [setMessages, isLoading, handleSendMessageInner]);

  // Proposed records (from chat attachments) — track per-record save status
  // and edits on the assistant message so the card survives re-renders.
  const handleProposedRecordStatus = useCallback((messageId: string, recordId: string, status: import("@researcher/shared").ProposedRecordStatus) => {
    setMessages((prev) => prev.map((m) =>
      m.id === messageId
        ? {
            ...m,
            proposedRecordStatuses: {
              ...(m.proposedRecordStatuses || {}),
              [recordId]: status,
            },
          }
        : m
    ));
    // When a lab gets saved, the next chat turn benefits from refreshed context.
    if (status === 'saved') {
      setHealthRefreshTrigger((n) => n + 1);
    }
  }, [setMessages]);

  const handleProposedRecordEdit = useCallback((messageId: string, recordId: string, data: import("@researcher/shared").ProposedLabRecord) => {
    setMessages((prev) => prev.map((m) =>
      m.id === messageId
        ? {
            ...m,
            proposedRecordEdits: {
              ...(m.proposedRecordEdits || {}),
              [recordId]: data,
            },
          }
        : m
    ));
  }, [setMessages]);

  // Tracking protocol proposal card — same pattern as profile updates.
  const handleTrackingProtocolStatus = useCallback((messageId: string, status: 'applied' | 'dismissed') => {
    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, trackingProtocolStatus: status } : m
    ));

    if (status === 'applied' && !isLoading) {
      setTimeout(() => {
        handleSendMessageInner('[User accepted the tracking protocol]', { skipExtraction: true, isNudge: true });
      }, 600);
    }
  }, [setMessages, isLoading, handleSendMessageInner]);

  // Regenerate last assistant response
  const handleRegenerate = useCallback(() => {
    setMessages((prev) => {
      // Find the last user message, remove it and all messages after it
      const lastUserIdx = prev.map(m => m.role).lastIndexOf('user');
      if (lastUserIdx === -1) return prev;
      const lastUserContent = prev[lastUserIdx].content;
      // Schedule re-send after state update
      setTimeout(() => handleSendMessage(lastUserContent), 0);
      return prev.slice(0, lastUserIdx);
    });
  }, [handleSendMessage, setMessages]);

  // Submit or remove feedback (fire-and-forget)
  const handleFeedback = useCallback((messageId: string, value: 'up' | 'down', reasons?: string[], text?: string) => {
    if (!currentChatId) return;
    submitFeedback(currentChatId, messageId, value, reasons, text).catch((err) =>
      console.error('[Chat] Failed to submit feedback:', err)
    );
  }, [currentChatId]);

  const handleRemoveFeedback = useCallback((messageId: string) => {
    if (!currentChatId) return;
    removeFeedback(currentChatId, messageId).catch((err) =>
      console.error('[Chat] Failed to remove feedback:', err)
    );
  }, [currentChatId]);

  // Run deep research from a suggestion chip click
  const handleDeepResearch = useCallback(async (messageId: string, query: string, focusArea: string) => {
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(apiUrl('/chat/deep-research'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, focusArea }),
      });

      if (!res.ok) throw new Error('Failed to run deep research');

      const data = await res.json();
      if (data.success && data.data) {
        setMessages((prev) => prev.map((m) =>
          m.id === messageId
            ? { ...m, researchCard: data.data }
            : m
        ));
      }
    } catch (err) {
      console.error('[Chat] Failed to run deep research:', err);
    }
  }, [currentUser]);

  // Generate artifact from a suggestion chip click
  const handleGenerateArtifact = useCallback(async (messageId: string, artifactType: string) => {
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;

      // Gather recent conversation context for the artifact
      const recentUserMessages = messages
        .filter(m => m.role === 'user')
        .slice(-5)
        .map(m => m.content)
        .join('\n\n');

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(apiUrl('/artifacts/generate'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ type: artifactType, context: recentUserMessages }),
      });

      if (!res.ok) throw new Error('Failed to generate artifact');

      const data = await res.json();
      if (data.success && data.data) {
        setMessages((prev) => prev.map((m) =>
          m.id === messageId
            ? { ...m, artifact: data.data.content || data.data }
            : m
        ));
      }
    } catch (err) {
      console.error('[Chat] Failed to generate artifact from suggestion:', err);
    }
  }, [currentUser, messages]);

  // Add files to the pending-attachments list. Validates each and rejects
  // friendly with a single inline error rather than silently dropping bad ones.
  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setAttachmentError(null);

    // Surface the retention policy the first time a user attaches anything,
    // so they know going in that the raw image will be auto-deleted at 48h.
    // localStorage key persists per browser; native users see it once per
    // install (good — different device, different reminder).
    try {
      if (!localStorage.getItem('attachment_retention_notice_shown')) {
        const hasImage = arr.some((f) => f.type?.startsWith('image/'));
        if (hasImage) {
          setRetentionNotice('Heads up: uploaded images auto-delete after 48 hours. Anything Romy extracts (labs, meds, etc.) stays saved.');
          localStorage.setItem('attachment_retention_notice_shown', '1');
        }
      }
    } catch { /* localStorage can throw in some private modes — silently skip */ }

    setPendingAttachments((prev) => {
      const remaining = MAX_ATTACHMENTS_PER_MESSAGE - prev.length;
      if (remaining <= 0) {
        setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
        return prev;
      }
      const toAdd: PendingAttachment[] = [];
      for (const file of arr.slice(0, remaining)) {
        const v = validateFile(file);
        if (!v.valid) {
          setAttachmentError(v.error);
          continue;
        }
        toAdd.push({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          previewUrl: URL.createObjectURL(file),
          status: 'pending',
        });
      }
      return [...prev, ...toAdd];
    });
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      pendingAttachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    e.target.value = ''; // allow re-picking the same file
  };

  // Native camera path. The HTML `<input type=file capture=environment>` hint
  // gets ignored by Capacitor's WebView on Android and the system shows the
  // photo picker instead — so on native we go through the Capacitor Camera
  // plugin which gives us an explicit camera-only source.
  const handleNativeCamera = useCallback(async () => {
    if (pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE) return;
    setAttachmentError(null);
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.DataUrl,
        quality: 85,
        allowEditing: false,
        saveToGallery: false,
        correctOrientation: true,
      });
      if (!photo.dataUrl) return;
      const blob = await (await fetch(photo.dataUrl)).blob();
      const ext = photo.format || 'jpg';
      const file = new File([blob], `camera_${Date.now()}.${ext}`, { type: blob.type || `image/${ext}` });
      addFiles([file]);
    } catch (err: any) {
      // User cancellation throws a non-fatal error — swallow silently.
      const msg = String(err?.message || err || '').toLowerCase();
      if (msg.includes('cancel') || msg.includes('denied')) return;
      console.error('Camera capture failed:', err);
      reportError(err, 'chat', { op: 'native_camera', platform: Capacitor.getPlatform() });
      setAttachmentError('Could not access the camera. Check app permissions.');
    }
  }, [addFiles, pendingAttachments.length]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  }, [isDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleSend = async () => {
    const text = input;
    const hasAttachments = pendingAttachments.length > 0;
    if (!text.trim() && !hasAttachments) return;

    // Attachments require authentication — Storage rules check ownership
    // against the path's userId segment.
    if (hasAttachments && !currentUser) {
      setAttachmentError('Please sign in to attach files.');
      return;
    }

    // Upload first so the bubble can render thumbnails by storagePath.
    let resolvedAttachments: ChatAttachment[] | undefined;
    let previewUrlMap: Record<string, string> | undefined;
    if (hasAttachments && currentUser) {
      // Mark items uploading + clear from preview so user sees the "send happened"
      setPendingAttachments((prev) => prev.map((a) => ({ ...a, status: 'uploading' as const })));
      const messageId = `${Date.now()}_pending`;
      try {
        const uploaded = await Promise.all(
          pendingAttachments.map((a) => uploadAttachment(a.file, currentUser.uid, messageId))
        );
        resolvedAttachments = uploaded;
        previewUrlMap = Object.fromEntries(
          uploaded.map((att, i) => [att.storagePath, pendingAttachments[i].previewUrl])
        );
        // Clear pending list — bubble now owns the object URLs via attachmentPreviewUrls
        setPendingAttachments([]);
      } catch (err) {
        console.error('[Chat] Attachment upload failed:', err);
        // Capture to Sentry with the real Firebase error code + file/platform
        // context — upload failures are otherwise invisible on a user's device.
        reportError(err, 'chat', {
          op: 'attachment_upload',
          platform: Capacitor.getPlatform(),
          code: String((err as { code?: string })?.code || 'unknown'),
          count: String(pendingAttachments.length),
          mimeTypes: pendingAttachments.map((a) => a.file.type || 'empty').join(','),
          totalBytes: String(pendingAttachments.reduce((n, a) => n + a.file.size, 0)),
        });
        setAttachmentError('Could not upload your file. Please try again.');
        setPendingAttachments((prev) => prev.map((a) => ({ ...a, status: 'error' as const, error: 'Upload failed' })));
        return;
      }
    }

    await handleSendMessage(text, {
      ...(resolvedAttachments && { attachments: resolvedAttachments }),
      ...(previewUrlMap && { attachmentPreviewUrls: previewUrlMap }),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Allow typing while Romy is responding, but don't send a second
      // request until the first finishes. canSend (which includes !isLoading)
      // gates the send button the same way.
      if (isLoading) return;
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      const maxH = 200;
      inputRef.current.style.height = '0px';
      const scrollH = inputRef.current.scrollHeight;
      inputRef.current.style.height = `${Math.min(scrollH, maxH)}px`;
      inputRef.current.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
    }
  }, [input]);

  // Handle topic parameter - update input and focus when topic is present
  useEffect(() => {
    if (topic) {
      const topicMessage = `I want to learn more about ${topic}...`;
      setInput(topicMessage);
      // Focus input after state update
      setTimeout(() => {
        inputRef.current?.focus();
        // Clear the topic parameter from URL after setting the input
        setSearchParams({}, { replace: true });
      }, 0);
    }
  }, [topic, setSearchParams]);

  // Handle prompt parameter - auto-send based on prompt type
  useEffect(() => {
    if (prompt && prompt !== processedPromptRef.current && !isLoading) {
      processedPromptRef.current = prompt;
      // Clear the prompt parameter from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("prompt");
      setSearchParams(newParams, { replace: true });

      // Continue the current conversation — do NOT reset. These prompts
      // (meal-plan tweak/prep reminders, "what's my diagnosis") are
      // continuations; resetting here spawned a truncated duplicate chat next
      // to the original. The active meal plan is injected server-side, so
      // appending keeps full context.
      const messageToSend = prompt === "diagnosis"
        ? "What is my latest diagnosis?"
        : prompt;
      setTimeout(() => handleSendMessage(messageToSend), 0);
    }
  }, [prompt, setSearchParams, searchParams, isLoading, handleSendMessage]);

  // Handle initial message parameter - auto-send the message
  // If a landing preview is provided, seed the conversation instead of re-sending
  // Always starts a new chat — never appends to an existing conversation
  useEffect(() => {
    if (initialMessage && !isLoading && initialMessage !== processedMessageRef.current) {
      processedMessageRef.current = initialMessage;
      // Clear the params from URL immediately
      setSearchParams({}, { replace: true });

      // Start fresh if there's an existing chat
      if (messages.length > 0 || currentChatId) resetChatSession();

      if (landingPreview) {
        // Seed conversation with the user message + Romy's preview response from the landing page
        const userMsg: Message = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: initialMessage,
          timestamp: new Date(),
        };
        const assistantMsg: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: landingPreview,
          timestamp: new Date(),
        };
        setTimeout(() => setMessages([userMsg, assistantMsg]), 0);
      } else {
        // No preview — send the message fresh
        setTimeout(() => handleSendMessage(initialMessage, skipExtractionParam ? { skipExtraction: true } : undefined), 0);
      }
    }
  }, [initialMessage, landingPreview, isLoading, handleSendMessage, setSearchParams, setMessages, currentChatId, resetChatSession]);

  // Handle prefill parameter - populate input without sending
  useEffect(() => {
    if (prefill) {
      setInput(prefill);
      setSearchParams({}, { replace: true });
    }
  }, [prefill, setSearchParams]);

  // Handle ?chatId=... — deep-link from Documents page back to source chat
  useEffect(() => {
    if (!deepLinkChatId) return;
    if (processedDeepLinkChatIdRef.current === deepLinkChatId) return;
    if (!currentUser) return;
    processedDeepLinkChatIdRef.current = deepLinkChatId;
    loadChat(deepLinkChatId);
    const next = new URLSearchParams(searchParams);
    next.delete('chatId');
    setSearchParams(next, { replace: true });
    // loadChat is stable enough; we intentionally only re-run on deepLinkChatId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkChatId, currentUser]);

  // Handle postId parameter — "Ask Romy" from community post
  useEffect(() => {
    if (postId && postId !== processedPostIdRef.current && !isLoading) {
      processedPostIdRef.current = postId;
      communityPostIdRef.current = postId;
      const title = postTitle || 'this post';
      // Clear params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("postId");
      newParams.delete("postTitle");
      setSearchParams(newParams, { replace: true });

      if (messages.length > 0) resetChatSession();

      setTimeout(() => handleSendMessage(`I saw a community post about "${decodeURIComponent(title)}" — can you help me understand it from a health perspective?`), 0);
    }
  }, [postId, postTitle, messages.length, isLoading, handleSendMessage, searchParams, setSearchParams, resetChatSession]);

  // Auto-dismiss health toast
  useEffect(() => {
    if (!healthToast) return;
    const timer = setTimeout(() => setHealthToast(null), 5000);
    return () => clearTimeout(timer);
  }, [healthToast]);

  // Auto-dismiss appointment toast
  useEffect(() => {
    if (!appointmentToast) return;
    const timer = setTimeout(() => setAppointmentToast(null), 5000);
    return () => clearTimeout(timer);
  }, [appointmentToast]);

  // Listen for chat-send from bottom tab bar (Romy button doubles as send on mobile)
  useEffect(() => {
    const handler = () => {
      if (input.trim() && !isLoading) {
        handleSendMessage(input);
      }
    };
    window.addEventListener('chat-send', handler);
    return () => window.removeEventListener('chat-send', handler);
  }, [input, isLoading, handleSendMessage]);

  // Memoize sidebar so it doesn't re-render on every streaming chunk
  const desktopSidebar = useMemo(() => {
    if (!currentUser) return null;
    return (
      <div
        className="hidden lg:flex w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl flex-col flex-shrink-0 self-stretch"
      >
        {/* New Chat button */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={startNewChat}
            className="w-full px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all flex items-center justify-center space-x-2"
          >
            <Plus size={18} weight="bold" />
            <span>New Chat</span>
          </button>
        </div>

        {/* Chat history (scrollable) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingChats ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Loading chats...
            </div>
          ) : chatHistory.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
              No chat history yet. Start a conversation!
            </div>
          ) : (
            <div className="p-2">
              {chatHistory.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => loadChat(chat.id)}
                  className={`
                    p-3 mb-2 rounded-lg cursor-pointer transition-colors
                    ${currentChatId === chat.id
                      ? 'bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 border-2 border-orange-300 dark:border-orange-700'
                      : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {chat.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {chat.updatedAt.toLocaleDateString()} {chat.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => handleBuildPacket(chat.id, e)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950 rounded transition-colors flex-shrink-0"
                        title="Prep for appointment"
                      >
                        <CalendarCheck size={16} weight="regular" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteChat(chat.id, e)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                        title="Delete chat"
                      >
                        <Trash size={16} weight="regular" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Health summary (pinned bottom) */}
        <HealthSummaryPanel refreshTrigger={healthRefreshTrigger} />
      </div>
    );
  }, [currentUser, chatHistory, currentChatId, healthRefreshTrigger]);

  const renderInputArea = () => {
    const canSend = (input.trim().length > 0 || pendingAttachments.length > 0) && !isLoading;
    return (
    <div className="px-3 md:px-4 pb-3 md:pb-4 pt-2 max-w-3xl w-full mx-auto" style={{ paddingBottom: keyboardHeight > 0 ? keyboardHeight + 12 : isNative ? 'max(0.75rem, env(safe-area-inset-bottom))' : undefined }}>
      {attachmentError && (
        <div className="mb-2 px-3 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <span>{attachmentError}</span>
          <button onClick={() => setAttachmentError(null)} className="ml-2 text-red-400 hover:text-red-600" aria-label="Dismiss">
            <X size={12} weight="bold" />
          </button>
        </div>
      )}
      <AttachmentPreview attachments={pendingAttachments} onRemove={removeAttachment} />
      <div className="flex items-end gap-2">
        <div
          className={`relative flex-1 border rounded-2xl bg-white dark:bg-gray-800 shadow-sm focus-within:ring-1 focus-within:ring-pink-400/30 transition-all ${
            isDragOver
              ? 'border-pink-400 dark:border-pink-500 ring-2 ring-pink-400/40'
              : 'border-gray-200 dark:border-gray-700 focus-within:border-pink-400 dark:focus-within:border-pink-500'
          }`}
          onDragOver={currentUser ? handleDragOver : undefined}
          onDragLeave={currentUser ? handleDragLeave : undefined}
          onDrop={currentUser ? handleDrop : undefined}
        >
          {/* Hidden inputs for paperclip + camera */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={handleFilePick}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFilePick}
            className="hidden"
          />

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            onPaste={handlePaste}
            placeholder={pendingAttachments.length > 0 ? "Add a note (optional)…" : "Message Romy..."}
            className="block w-full resize-none bg-transparent pl-20 pr-20 py-[10px] text-base text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
            rows={1}
            style={{ maxHeight: '200px', overflowY: 'hidden' }}
          />

          {/* Left side: paperclip + camera */}
          {currentUser && (
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Attach a file"
                title="Attach an image or PDF"
              >
                <Paperclip size={18} weight="bold" />
              </button>
              {/* Camera button: on native (Capacitor) we route through the
                  Camera plugin so we get an explicit camera-only source —
                  capture="environment" is ignored by Android WebView and
                  shows the gallery picker. On mobile web we still fall back
                  to the input's capture attribute. Hidden on desktop. */}
              {(isNative || mobileOS) && (
                <button
                  type="button"
                  onClick={isNative ? handleNativeCamera : () => cameraInputRef.current?.click()}
                  disabled={pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                  className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Take a photo"
                  title="Take a photo"
                >
                  <Camera size={18} weight="bold" />
                </button>
              )}
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            <VoiceInputButton
              onTranscript={(text) => setInput((prev) => (prev ? prev + ' ' + text : text))}
              onError={setVoiceError}
            />
            {canSend && (
              <button
                onClick={handleSend}
                className="p-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 shadow-sm transition-all"
              >
                <ArrowUp size={18} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 text-center">
        Romy is your advocate, not your doctor.
      </p>
    </div>
    );
  };

  return (
    <div
      className="h-screen md:h-screen bg-white dark:bg-gray-900 md:overflow-hidden flex flex-col"
    >
      <PageMeta
        title="Chat with Romy — AI Health Companion"
        description="Get personalized health insights from Romy, your AI women's health companion. Ask about endometriosis, PCOS, PMDD, fertility, and more."
        path="/chat"
      />
      <AppNav />

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
      <AIDataConsentModal isOpen={showAIConsent} onConsent={handleAIConsent} onDecline={handleAIConsentDecline} />

      {/* Main Content */}
      <div className={`flex-1 min-h-0 pt-16 md:pb-0 ${keyboardHeight > 0 ? 'pb-0' : 'pb-[100px]'}`}>
      <div className="max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8 py-0 md:py-4 h-full flex flex-col">
        <div className="flex gap-0 md:gap-6 flex-1 min-h-0">
          {/* Chat History Sidebar — desktop only */}
          {desktopSidebar}

          {/* Mobile chat history bottom sheet */}
          <BottomSheet open={!!(currentUser && isSidebarOpen)} onClose={() => setIsSidebarOpen(false)} title="Chat History">
            {/* New Chat button */}
            <div className="pb-3">
              <button
                onClick={startNewChat}
                className="w-full px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all flex items-center justify-center space-x-2"
              >
                <Plus size={18} weight="bold" />
                <span>New Chat</span>
              </button>
            </div>
            {/* Chat history */}
            {isLoadingChats ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Loading chats...
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                No chat history yet. Start a conversation!
              </div>
            ) : (
              <div className="space-y-2">
                {chatHistory.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => loadChat(chat.id)}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-colors
                      ${currentChatId === chat.id
                        ? 'bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 border-2 border-orange-300 dark:border-orange-700'
                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {chat.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {chat.updatedAt.toLocaleDateString()} {chat.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={(e) => handleBuildPacket(chat.id, e)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950 rounded transition-colors flex-shrink-0"
                          title="Prep for appointment"
                        >
                          <CalendarCheck size={16} weight="regular" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteChat(chat.id, e)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                          title="Delete chat"
                        >
                          <Trash size={16} weight="regular" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </BottomSheet>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Chat Title — hidden on mobile */}
            <div className="hidden md:block mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 mb-2">
                  {currentUser && (
                    <button
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Toggle chat history"
                    >
                      <Sidebar size={24} weight="regular" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Chat Container */}
            <div
              className={`overflow-hidden flex flex-col md:flex-1 md:min-h-0 ${
                currentUser && !isNative
                  ? 'h-[calc(100dvh-72px)]'
                  : keyboardHeight > 0
                    ? 'h-[calc(100dvh-72px)]'
                    : 'h-[calc(100dvh-64px-72px)]'
              }`}
            >
              {/* Unauthenticated nudge — desktop: sign in, mobile: get the app */}
              {!currentUser && (
                <>
                  {/* Desktop: sign in prompt */}
                  <div className="hidden md:block sticky top-0 z-10 p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-gray-900 dark:to-gray-900 border-b border-orange-200 dark:border-orange-800 shadow-sm">
                    <div className="flex items-start space-x-2">
                      <span className="text-pink-500 flex-shrink-0 mt-0.5"><RomyIcon size={20} /></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-100 mb-1">
                          Get more from Romy
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 leading-relaxed">
                          Sign in to save your chat history and get personalized insights.
                        </p>
                        <button
                          onClick={() => setIsLoginOpen(true)}
                          className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-medium rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-sm hover:shadow-md"
                        >
                          Sign In
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Mobile: download app prompt (dismissable, resets each new convo) */}
                  {!appBannerDismissed && <AppBadgeBanner onDismiss={() => setAppBannerDismissed(true)} />}
                </>
              )}

              {/* Mobile chat toolbar — history + new chat */}
              {currentUser && (
                <div className="md:hidden flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800">
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ClockCounterClockwise size={18} weight="regular" />
                    <span>History</span>
                  </button>
                  <button
                    onClick={startNewChat}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <Plus size={18} weight="bold" />
                    <span>New Chat</span>
                  </button>
                </div>
              )}

              {/* Intake express-lane nudge — opt-in, only for empty-profile users */}
              {currentUser && profileEmpty && !intakeDismissed && (
                <div className="flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-950/20 dark:to-pink-950/20 border-b border-pink-100 dark:border-pink-900">
                  <Sparkle size={18} weight="duotone" className="flex-shrink-0 text-pink-500" />
                  <p className="flex-1 min-w-0 text-xs text-gray-600 dark:text-gray-300">
                    Want Romy to know your history up front?{' '}
                    <button onClick={() => navigate('/intake')} className="font-semibold text-pink-600 dark:text-pink-400 hover:underline">
                      Set up your profile
                    </button>{' '}
                    <span className="text-gray-400">(3 min) — or just keep chatting.</span>
                  </p>
                  <button
                    onClick={() => { try { localStorage.setItem('romy_intake_dismissed', 'true'); } catch { /* ignore */ } setIntakeDismissed(true); }}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Get the App nudge (signed-in mobile web only) */}
              {!isNative && currentUser && (
                <div className="md:hidden flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-gray-900 dark:to-gray-900 border-b border-orange-200 dark:border-orange-800">
                  <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">Get the app</span>
                  {mobileOS !== 'android' && APP_STORE_URL && (
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2.5 py-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg whitespace-nowrap">
                      {IOS_ICON}
                      <span className="text-[10px] font-semibold leading-none">App Store</span>
                    </a>
                  )}
                  {mobileOS !== 'ios' && (
                    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2.5 py-1.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg whitespace-nowrap">
                      {ANDROID_ICON}
                      <span className="text-[10px] font-semibold leading-none">Google Play</span>
                    </a>
                  )}
                </div>
              )}


              {/* Empty state: greeting + input anchored to bottom on mobile to avoid keyboard reflow */}
              {messages.length === 0 && !isLoading ? (
                <>
                <div className="flex-1 min-h-0 flex flex-col items-center justify-end md:justify-center px-4 pb-2 md:pb-0">
                  <div className="max-w-2xl w-full flex flex-col items-center text-center">
                    {/* Message with avatar — same layout as AssistantMessage */}
                    {(!currentUser || openingLoaded) ? (
                    <>
                    <div className="w-full max-w-md flex justify-start px-2 animate-fadeIn">
                      <div className="hidden md:block flex-shrink-0 mt-3 mr-3 text-pink-500 animate-bloom">
                        <RomyIcon size={22} weight="fill" />
                      </div>
                      <div className="flex-1">
                        <div className="md:hidden mb-1.5 text-pink-500 animate-bloom">
                          <RomyIcon size={22} weight="fill" />
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-pink-100 dark:border-pink-800/30 p-4 text-left">
                          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                            {openingMessage
                              || (currentUser?.displayName
                                ? userGoalDescs.length > 0
                                  ? `Hey ${currentUser.displayName.split(' ')[0]}! How's "${userGoalDescs[0]}" going? Or what else is on your mind?`
                                  : `Hey ${currentUser.displayName.split(' ')[0]}! What's on your mind today?`
                                : "Hey! I'm Romy (rhymes with homie). What's on your mind?")}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Chips */}
                    <div className="mt-4 flex flex-wrap justify-center gap-2 max-w-md animate-fadeIn">
                      {(openingChips.length > 0
                        ? openingChips
                        : getSuggestedPrompts(userConditions, userGoalDescs, reproductiveStatus, !!currentUser)
                      ).map((chip) => (
                        <button
                          key={chip}
                          onClick={() => handleSendMessage(chip)}
                          className="px-3 py-1.5 text-sm text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20
                                     border border-pink-200 dark:border-pink-800 rounded-full
                                     hover:bg-pink-100 hover:border-pink-300
                                     dark:hover:bg-pink-900/40 dark:hover:border-pink-700
                                     transition-colors font-medium"
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                    </>
                    ) : (
                    /* Skeleton placeholder — reserves space while loading to prevent input jump */
                    <div className="w-full max-w-md px-2 animate-pulse">
                      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-pink-100 dark:border-pink-800/30 p-4">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                      </div>
                      <div className="mt-4 flex justify-center gap-2">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-28" />
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-36" />
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-full w-24" />
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                {renderInputArea()}
                </>
              ) : (
                <>
                  {/* Messages with bottom fade */}
                  <div className="relative flex-1 min-h-0">
                  <div
                    ref={messagesContainerRef}
                    className="h-full overflow-y-auto p-3 md:p-6 pb-20 md:pb-24 space-y-6 md:space-y-8"
                    style={{ overflowAnchor: 'none' }}
                  >
                    {messages.map((message, idx) => {
                      const isLastAssistant = message.role === 'assistant' &&
                        !messages.slice(idx + 1).some(m => m.role === 'assistant');
                      return (
                        <ChatMessageItem
                          key={message.id}
                          message={message}
                          onNavigate={navigate}
                          onGenerateArtifact={handleGenerateArtifact}
                          onDeepResearch={handleDeepResearch}
                          onProfileUpdateStatus={handleProfileUpdateStatus}
                          onTrackingProtocolStatus={handleTrackingProtocolStatus}
                          onProposedRecordStatus={handleProposedRecordStatus}
                          onProposedRecordEdit={handleProposedRecordEdit}
                          onLoginClick={!currentUser ? () => setIsLoginOpen(true) : undefined}
                          isLastAssistant={isLastAssistant}
                          isStreaming={isStreaming}
                          chatId={currentChatId}
                          onRegenerate={handleRegenerate}
                          onFeedback={handleFeedback}
                          onRemoveFeedback={handleRemoveFeedback}
                          onSelectReply={handleSendMessage}
                        />
                      );
                    })}

                    {isLoading && !isStreaming && (
                      <LoadingDots />
                    )}
                    {isThinking && (
                      <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400 dark:text-gray-500 animate-pulse">
                        <CircleNotch size={14} className="animate-spin" />
                        {thinkingLabel}
                      </div>
                    )}

                    {/* Spacer: gives the scroll container enough room BELOW the
                        latest message that it can scroll the user's message all
                        the way to the top of the viewport.

                        Required height ≈ container height − user message height.
                        On desktop the chat area is ~70-80vh, so we need ~70vh of
                        empty room below to allow scroll-to-top. Anything smaller
                        and the browser caps maxScrollTop, leaving the user msg
                        partway up — and as the assistant streams in below, the
                        smooth-scroll catches up toward the now-reachable target,
                        producing a visible bounce.

                        Always rendered so the scrollable height stays consistent
                        across turns (removing/changing it would cause snap). */}
                    <div className="min-h-[75vh]" aria-hidden="true" />
                  </div>
                  {/* Bottom fade — text appears to slide under the input bar */}
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-900 to-transparent" />
                  </div>

                  {/* Health data tracked toast */}
                  {healthToast && (
                    <button
                      onClick={() => {
                        navigate(healthToast.type === 'cycle' ? '/health/cycle' : '/health');
                        setHealthToast(null);
                      }}
                      className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-pink-50 to-orange-50 dark:from-gray-900 dark:to-gray-900 border-t border-pink-200 dark:border-pink-800 animate-fadeIn group"
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        {healthToast.type === 'cycle'
                          ? <Drop size={16} weight="fill" className="text-pink-500 flex-shrink-0" />
                          : <Heartbeat size={16} weight="fill" className="text-pink-500 flex-shrink-0" />
                        }
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                          {healthToast.type === 'cycle' ? 'Cycle data tracked' : 'Health data tracked'}
                          {healthToast.symptoms.length > 0 && (
                            <span className="text-gray-500 dark:text-gray-400 font-normal">
                              {' · '}{healthToast.symptoms.slice(0, 2).map(s => s.name).join(', ')}
                              {healthToast.symptoms.length > 2 && ` +${healthToast.symptoms.length - 2}`}
                            </span>
                          )}
                          {healthToast.mood && (
                            <span className="text-gray-500 dark:text-gray-400 font-normal">
                              {' · '}{healthToast.mood === 'great' ? '😊' : healthToast.mood === 'good' ? '🙂' : healthToast.mood === 'okay' ? '😐' : healthToast.mood === 'low' ? '😔' : '😢'}
                            </span>
                          )}
                          {(healthToast.flowLevel ? healthToast.flowLevel !== 'none' : healthToast.bleeding) && (
                            <span className="text-gray-500 dark:text-gray-400 font-normal"> · {healthToast.flowLevel === 'spotting' ? '💧' : '🩸'}</span>
                          )}
                        </span>
                      </div>
                      <ArrowRight size={14} className="text-gray-400 dark:text-gray-500 group-hover:text-pink-500 flex-shrink-0 ml-2 transition-colors" />
                    </button>
                  )}

                  {/* Appointment extracted toast */}
                  {appointmentToast && (
                    <button
                      onClick={() => {
                        navigate('/appointments');
                        setAppointmentToast(null);
                      }}
                      className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-pink-50 to-orange-50 dark:from-gray-900 dark:to-gray-900 border-t border-pink-200 dark:border-pink-800 animate-fadeIn group"
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <CalendarBlank size={16} weight="fill" className="text-pink-500 flex-shrink-0" />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                          Appointment added: {appointmentToast.title}
                          <span className="text-gray-500 dark:text-gray-400 font-normal">
                            {' · '}{new Date(appointmentToast.date + 'T00:00:00').toLocaleDateString()}
                          </span>
                        </span>
                      </div>
                      <ArrowRight size={14} className="text-gray-400 dark:text-gray-500 group-hover:text-pink-500 flex-shrink-0 ml-2 transition-colors" />
                    </button>
                  )}

                  {renderInputArea()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <GentleToast message={deleteError} onDismiss={() => setDeleteError(null)} />
      <GentleToast message={voiceError} onDismiss={() => setVoiceError(null)} />
      <GentleToast message={retentionNotice} duration={8000} onDismiss={() => setRetentionNotice(null)} />

      <NotificationOptIn />

      <ConfirmModal
        open={!!deleteChatId}
        title="Delete Chat"
        message="Are you sure you want to delete this chat? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={confirmDeleteChat}
        onCancel={() => setDeleteChatId(null)}
      />
    </div>
  );
}
